import { Check, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * The release decision, read as a decision.
 *
 * The gate is the most consequential screen in the product - it is the claim
 * that a package is fit to hand over - and it was rendering as a run-on of
 * check/pass/detail triples. Each criterion is now its own line with its
 * verdict and the evidence sentence the backend wrote for it, because the
 * detail is the part that survives scrutiny.
 *
 * The overall verdict is never inferred from counting green rows: it is the
 * backend's own `passed`, and a run can have every criterion pass and still
 * not be released.
 */

export interface GateCheck {
  check: string;
  pass: boolean;
  detail?: string;
}

export interface Gate {
  passed: boolean;
  checks: GateCheck[];
  /**
   * Whether the LEDGER recorded this verdict. Passing is not being released.
   *
   * "Only a sealed evaluation moves a run to GATE_PASSED, so a response with
   * `passed: true` and `sealed: false` means the checks hold and the release
   * did not happen." Undefined where the response did not carry it.
   */
  sealed?: boolean;
  /**
   * Why the ledger did not record it.
   *
   * The contract says why this field exists, in as many words: "a 200 carrying
   * `passed: true` over a run that stays pending is an answer a client can
   * neither act on nor explain". It was added to answer exactly the question
   * this screen was telling operators it could not answer.
   */
  sealRefusal?: string | null;
}

export function readGate(value: unknown): Gate | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.checks)) return null;
  return {
    passed: v.passed === true,
    // Read, not inferred. `sealed` absent is not `sealed: false`: the GET does
    // not carry it and only the POST records a verdict, so an absent field
    // means unknown and must not be drawn as a refusal.
    sealed: typeof v.sealed === 'boolean' ? v.sealed : undefined,
    sealRefusal: typeof v.sealRefusal === 'string' ? v.sealRefusal : null,
    checks: (v.checks as Record<string, unknown>[]).map((c) => ({
      check: String(c.check ?? ''),
      pass: c.pass === true,
      detail: typeof c.detail === 'string' ? c.detail : undefined,
    })),
  };
}

/**
 * What to do about a criterion that is not met.
 *
 * The gate names the shortfall and the operator is left holding it: "102 flags
 * await a human keep/drop decision" states a fact and not a next move, and the
 * step that clears it is three panels up the page. Every failure that a person
 * can actually clear now says how.
 */
/**
 * No remedy may state a quantity, because this component reads none.
 *
 * "Approve the four documents above" was written when there were four. The
 * count comes from the drafted document list, which lives on the step card and
 * never reaches here, so the sentence was a number the screen could not check
 * against anything - on the one surface whose whole purpose is that every
 * figure is backed. The document step had exactly this line and it was fixed
 * there by reading `all.length`; the gate cannot, so it says no number.
 *
 * The criterion's own `detail` is the backend's sentence and does carry the
 * figures. That is where a count belongs.
 */
export const REMEDY: Record<string, string> = {
  'Curation decided': 'Open "Worth leaving out?" above and decide them. Keeping all of them ships every file.',
  'Population dispositioned': 'Open "Take stock of every file" above. It runs in the background.',
  'Toolset selected': 'Choose what ships in step 1.',
  'Packaging present': 'Approve the documents above.',
  'Structure legible': 'Choose the layout in step 2.',
};

/**
 * Criteria whose fix lives in a step that has already been passed.
 *
 * The gate renders from EXPORTABLE onward and the step cards it points at are
 * unmounted by then, so four remedies named controls that were not on the
 * page. A criterion whose stated fix cannot be reached is worse than one with
 * no fix: it sends someone hunting for a button that does not exist.
 */
const EARLIER_STEP: Record<string, string> = {
  'Toolset selected': 'what ships',
  'Structure legible': 'the folder layout',
  'Packaging present': 'the documents',
};

/** States in which the gate is rendered but the earlier step cards are gone. */
const PAST_THE_STEPS = ['EXPORTABLE', 'GATE_PENDING', 'GATE_PASSED', 'GATE_FAILED', 'DELIVERING', 'DELIVERED', 'DELIVERY_FAILED'];

/**
 * The states in which a population can still be registered.
 *
 * packaging_register_population asserts STRUCTURING and nothing else, so
 * there is exactly one state in which a package can be given a manifest.
 * Naming the run leaves that state, and no path returns to it. Past it the
 * remedy above becomes a button that answers 422.
 *
 * A criterion whose only stated fix cannot be performed is worse than one with
 * no fix at all: it sends someone to press something and conclude the product
 * is broken. This is why the run's state decides what the remedy says.
 */
export const POPULATION_OPEN_STATES = ['STRUCTURING'];

/**
 * The backend's check names, said as an operator would.
 *
 * The remedy under each row was translated and the label above it was not, so
 * the most consequential screen in the product read "Population dispositioned"
 * in the one register the house rules keep off screen. Unmapped names fall
 * through readable rather than blank.
 */
const CHECK_LABEL: Record<string, string> = {
  'Population dispositioned': 'Every file accounted for',
  'Curation decided': 'Every flag decided',
  'Toolset selected': 'What ships is chosen',
  'Structure legible': 'The folders make sense',
  'Packaging present': 'The documents are approved',
  'Gaps resolved': 'Problems found are settled',
  'Conservation equation': 'The counts add up',
  'No deep clones': 'Nothing is duplicated',
  'Parenthood preserved': 'Files kept their containers',
  'Provenance applied': 'Every file says where it came from',
  'Reconstruction complete': 'The records are built',
};

export function remedyFor(check: string, runState?: string): string | null {
  if (check === 'Population dispositioned' && runState && !POPULATION_OPEN_STATES.includes(runState)) {
    return (
      'This run has gone past the point where a population can be registered: the database accepts it only while a ' +
      'run is structuring, and naming the package leaves that state for good. Nothing on this page can clear it. ' +
      'Start a new run for this bucket and take stock of every file before you name it.'
    );
  }
  if (EARLIER_STEP[check] && runState && PAST_THE_STEPS.includes(runState)) {
    return `This is decided in an earlier step and this run has moved past it, so nothing on this page can change ${EARLIER_STEP[check]}. Start a new run for this bucket to decide it differently.`;
  }
  return REMEDY[check] ?? null;
}

/** A criterion an operator can clear from this screen, and how. */
export interface GateAction {
  check: string;
  label: string;
  run: () => void;
  busy?: boolean;
}

export function GateChecks({
  gate,
  note,
  actions = [],
  outcome = null,
  runState,
}: {
  gate: Gate;
  note?: string;
  /** Decides whether a remedy is possible on this run at all. */
  runState?: string;
  /** Buttons rendered on the failing criterion itself. Telling someone to go
   *  and find a panel that is not on the page is how this screen dead-ended. */
  actions?: GateAction[];
  /** What happened when one of those buttons was last pressed, shown at the
   *  button. The page's own note renders four hundred lines higher, so a
   *  refused write looked like a dead button rather than a refusal. */
  outcome?: { check: string; message: string } | null;
}) {
  const failed = gate.checks.filter((c) => !c.pass).length;
  /**
   * Released means the LEDGER said so, not that the checks hold.
   *
   * This printed "Released" in green from `passed` alone, over a run sitting
   * at GATE_PENDING with no delivery - the exact claim the contract warns
   * against. A verdict a reviewer sees must never be stronger than the ledger.
   * Where the response carries no `sealed` (the GET does not), passing is
   * still not release, so it reads as not released rather than as released.
   */
  const released = gate.passed && gate.sealed === true;
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-line px-4 py-2.5">
        <span
          className={cn(
            'text-caption font-semibold uppercase tracking-wider',
            released ? 'text-ok' : failed > 0 ? 'text-warn' : 'text-ink-3',
          )}
        >
          {released ? 'Released' : failed > 0 ? `${failed} of ${gate.checks.length} criteria not met` : 'Not released'}
        </span>
        {!released && failed === 0 && (
          <span className="text-small text-ink-2">
            Every criterion passes. A release is sealed only with the full evidence bar behind it, so this run stops
            short of released.
          </span>
        )}
        {/* The reason, from the response, where there is one. This screen used
            to say it could not tell you why; the field answering that was
            being read off the wire and dropped. */}
        {gate.passed && gate.sealed === false && gate.sealRefusal && (
          <p role="status" className="mt-1 w-full rounded border border-warn/40 bg-warn-soft px-3 py-2 text-small text-ink-2">
            The ledger did not record this verdict, so the run is not released: {gate.sealRefusal}
          </p>
        )}
      </div>

      {gate.checks.map((c) => (
        <div key={c.check} className="flex items-baseline gap-3 border-b border-line px-4 py-2 text-small last:border-0">
          <span
            className={cn(
              'mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full border',
              c.pass ? 'border-ok/40 bg-ok/10 text-ok' : 'border-warn/40 bg-warn-soft text-warn',
            )}
            aria-hidden="true"
          >
            {c.pass ? <Check className="h-2.5 w-2.5" /> : <X className="h-2.5 w-2.5" />}
          </span>
          <span className="w-52 flex-none text-ink">{CHECK_LABEL[c.check] ?? c.check}</span>
          <span className="min-w-0 flex-1">
            <span className="block text-caption text-ink-3">{c.detail ?? ''}</span>
            {!c.pass &&
              (remedyFor(c.check, runState) ? (
                <span className="mt-0.5 block text-small text-acc-2">{remedyFor(c.check, runState)}</span>
              ) : (
                <span className="mt-0.5 block text-small text-ink-3">
                  This one is settled by the walk and the backend, so nothing on this page can clear it.
                </span>
              ))}
            {!c.pass && outcome?.check === c.check && (
              <span className="mt-1.5 block rounded border border-warn/40 bg-warn-soft px-2 py-1 text-caption text-ink-2">
                {outcome.message}
              </span>
            )}
            {!c.pass &&
              actions
                .filter((a) => a.check === c.check)
                .map((a) => (
                  <button
                    key={a.label}
                    onClick={a.run}
                    disabled={a.busy}
                    className="mt-1.5 rounded border border-acc bg-acc-soft px-2.5 py-1 text-caption text-acc-2 transition-colors duration-fast hover:bg-hov disabled:opacity-50"
                  >
                    {a.busy ? 'Working…' : a.label}
                  </button>
                ))}
          </span>
        </div>
      ))}

      {note && <p className="border-t border-line px-4 py-2 text-caption text-ink-4">{note}</p>}
    </div>
  );
}
